/**
 * Modal to pick an activity type (handler) when adding an activity.
 */

import { useEffect, useState } from "react";
import { Modal } from "../../primitives/Modal";
import { useActivityHandlers } from "../../../api/opsV2/hooks";
import { Loader2 } from "lucide-react";

type ActivityTypePickerModalProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (activityType: string, handlerVersion: number) => void;
};

const PLACEHOLDER_FILTER = "Filtrer par type ou description...";

export function ActivityTypePickerModal({ open, onClose, onSelect }: ActivityTypePickerModalProps) {
  const { data: handlers, loading, error } = useActivityHandlers();
  const [filter, setFilter] = useState("");

  const filtered = (handlers ?? []).filter(
    (h) =>
      !filter.trim() ||
      h.activity_type.toLowerCase().includes(filter.toLowerCase()) ||
      (h.description ?? "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <Modal open={open} onClose={onClose} title="Ajouter une activité">
      <div className="min-w-[320px]">
        <input
          type="text"
          placeholder={PLACEHOLDER_FILTER}
          className="w-full border border-border rounded px-2 py-1.5 text-sm mb-3"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement...
          </div>
        )}
        {error && <p className="text-sm text-destructive py-2">{error.message}</p>}
        {!loading && !error && (
          <ul className="space-y-1 max-h-64 overflow-auto">
            {filtered.length === 0 && <li className="text-sm text-muted py-2">Aucun handler trouvé.</li>}
            {filtered.map((h) => (
              <li key={`${h.activity_type}-${h.handler_version}`}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 rounded border border-border hover:bg-muted/50 text-sm"
                  onClick={() => onSelect(h.activity_type, h.handler_version)}
                >
                  <span className="font-medium">{h.activity_type}</span>
                  {h.description && <span className="block text-muted text-xs mt-0.5">{h.description}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
